import {
  DeliverMedicationConsultationUpsell,
  DoctorsNoteConsultationUpsell,
  Doxycycline15TreatmentsStdConsultationUpsell,
  Doxycycline30TreatmentsStdConsultationUpsell,
  FollowUpStdTestConsultationUpsell,
  LevonorgestrelConsultationUpsell,
  MensIntimateWashConsultationUpsell,
  OndansetronDissolvableTabletStdConsultationUpsell,
  OndansetronPillStdConsultationUpsell,
  PriorityProcessingConsultationUpsell,
  ProbioticsConsultationUpsell,
} from '@data/consultation-request-upsells';
import { Pharmacy } from '@enums/pharmacies';
import { Sites } from '@enums/sites';
import { UpsellSlugs } from '@enums/upsell-slugs';
import { AppEnvironment } from '@models/app-environment';

/**
 * Environment variables for use on the HealthLabs site, both in production and local environment.
 */
export const healthlabsCommonEnvironment = {
  site: Sites.Healthlabs,
  siteId: '4',
  domain: 'HealthLabs.com',
  faviconFile: 'healthlabs-favicon.ico',
  phone: '1 (800) 579-3914',
  email: 'support@healthlabs.com',
  businessHours: 'Monday - Sunday 8 a.m. - 8 p.m. (Central Standard Time - CST)',
  allowedRoutes: ['/*'],
  enablePayLater: false,
  enableBitPay: false,
  enableGiftCard: true,
  enableBusinessAccounts: true,
  applePayLabel: 'Analyte Health',
  order: {
    minAgeAllowed: 0,
    showCancellationPolicy: true,
    navigateToOrderAddressRoute: true,
    shouldShowPhoneFieldOnOrderRoute: false,
    hasPhysicianFee: true,
    physicianFeeInCents: 600,
    forceEmailResults: true,
    isPartnerTestingAllowed: true,
    orderPaymentFormLayout: 'horizontal',
    enableOrderUpgrade: false,
    enableDynamicOrderUpgrade: true,
    enableTosOnOrderPage: false,
    stdPanelWithMycoplasmaTestId: 3162,
    abTestsWithMycoplasmaTestId: [3377, 3379, 3376],
    comprehensiveWellnessPanelId: 3381,
    isFemaleCondomUpsellAllowed: true,
    triggerFemaleCondomUpsellConsultation: true,
    skipUpsellsForBusinessAccounts: false,
    orderSummary: {
      allowEditingCart: false,
      editCartButtonText: '',
      displayCartSummaryTableHeaders: true,
      testsPrefix: '',
      showCouponCode: true,
      displayFindTestsModal: true,
    },
    orderComplete: {
      displaySisterSiteBanner: false,
      testOrderedWording: {
        single: 'Test Ordered',
        plural: 'Tests Ordered',
      },
    },
  },
  maxAgeAllowed: 120,
  minimumNameLength: 2,
  disableSchedulingRedirectForTreatmentTypes: [],
  consultation: {
    deliveryPharmacyId: Pharmacy.TAHA,
    lastPeriodQuestionId: '97',
    erectileDysfunctionMedicationQuestionId: '234',
    offersEdTreatmentUpsell: true,
    highBloodPressureQuestionId: '228',
    breastfeedingQuestionIds: ['31', '82'],
  },
  ngFindalab: {
    findalabMapId: '838e4d0c76cf80ed',
    searchEndpoint: '/labs/nearPostalCode',
    filterByStates: null,
    inHomeCollectionAllowed: true,
    inHomeCollectionCostInCents: 11900,
    inHomeCollectionProviderIds: [1, 2],
    showPhone: false,
    filterByTestAndSite: true,
    getNetworksFromTests: true,
    verifyTestsCanBeOrdered: true,
    validateSelectedLabNetwork: true,
    searchTitle: 'Test Centers',
    searchDescription: undefined,
    pharmacySearchDescription:
      'To choose your pharmacy, enter your zip code into the search box below and click the search icon',
    locateMsg: 'Locate Me',
    labButtonText: 'Choose This Location',
    searchButtonText: '',
    showSearchIcon: true,
    labIconClass: 'fa fa-arrow-right ml-3 is-pulled-right',
    searchMessageNoResults:
      'Oops! Sorry, we could not find any testing centers near that location. Please try your search again with a different or less specific address.',
    inputGroup: {
      container: 'field has-addons',
      field: 'input control',
      button: 'control',
    },
    resultsZoom: 10,
    choosePharmacySearchTitle: '',
    showDayOfWeekFilter: true,
  },
  titles: {
    order: 'Order - HealthLabs.com',
    consultationRequest: 'Clinical Consultation & Prescription Request - HealthLabs.com',
    headlineCheckoutScheduled: 'Clinical Consultation & Prescription Request',
    step1Title: 'Find a Test Center',
    selectedCenterLabel: 'Lab',
    orderPageHeader: {
      title: '',
      subtitle: '',
    },
    scheduledConsultationRequest: {
      title: 'Clinical Phone Consultation & Prescription Request',
      calendarDetails:
        'Please use the calendar below to pick a date and time for your consultation. Nothing will be scheduled until you have picked a date and time.',
      phoneNumberDetails: 'NOTE: Someone from our clinical team will call you on',
      availabilityDetails:
        'Based on availability, you may receive the call 60 minutes before or after your scheduled time.',
    },
  },
  buttons: {
    checkout: 'Place My Order',
    selectedCenterChange: 'Change Location',
  },
  styles: {
    metaThemeColor: '#ffffff',
    chooseAPharmacy: {
      labButtonClass: 'is-primary',
      searchButtonClass: 'is-primary',
      googleMapsLabMarkerFillColor: '#5b90b3',
      googleMapsRecommendedMarkerFillColor: '#ffa500',
      googleMapsMarkerHoverFillColor: '#eb4d4c',
    },
    consultationRequest: {
      headlineClass: 'column is-offset-1',
    },
    healthLabsCoupon: {
      buttonColor: '',
    },
    orderCompleteInstructions: {
      cardContentClass: '',
    },
    orderBetterLab: {
      orderPlaceNotificationClass: 'is-success',
      currentTestCenterNotificationClass: 'is-primary',
      keepCurrentLabButtonClass: '',
      keepCurrentLabButtonColor: '',
    },
    testCenter: {
      labButtonClass: 'is-primary button animated-right',
      searchButtonClass: 'is-primary',
      googleMapsLabMarkerFillColor: '#5b90b3',
      googleMapsRecommendedMarkerFillColor: '#ffa500',
      googleMapsMarkerHoverFillColor: '#eb4d4c',
    },
  },
  shopperApproved: {
    site: 25124,
    token: '201H13Sj',
  },
  consultationRequestUpsells: {
    [UpsellSlugs.Probiotics]: ProbioticsConsultationUpsell,
    [UpsellSlugs.Levonorgestrel]: LevonorgestrelConsultationUpsell,
    [UpsellSlugs.OndansetronPill]: OndansetronPillStdConsultationUpsell,
    [UpsellSlugs.OndansetronDissolvableTablet]: OndansetronDissolvableTabletStdConsultationUpsell,
    [UpsellSlugs.MensIntimateWash]: MensIntimateWashConsultationUpsell,
    [UpsellSlugs.FollowUpStdTest]: FollowUpStdTestConsultationUpsell,
    [UpsellSlugs.DoctorsNote]: DoctorsNoteConsultationUpsell,
    [UpsellSlugs.DeliverMedication]: DeliverMedicationConsultationUpsell,
    [UpsellSlugs.Doxycycline15TreatmentsStd]: Doxycycline15TreatmentsStdConsultationUpsell,
    [UpsellSlugs.Doxycycline30TreatmentsStd]: Doxycycline30TreatmentsStdConsultationUpsell,
    [UpsellSlugs.PriorityProcessing]: PriorityProcessingConsultationUpsell,
  },
} satisfies Partial<AppEnvironment>;
